import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';
import ButtonCustom from 'components/ButtonCustom';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import ButtonIcon from 'components/ButtonIcon';
import CloseIcon from '@mui/icons-material/Close';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import { TypographyLocale } from 'components/TypographyLocale';
import UseAuthContext from 'context/AuthContext';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminTopUp, cancelTransaction, getOrganizationTransactions } from 'services/business-setting-service';
import moment from 'moment';
import shortid from 'shortid';
import { USER_ROLES } from 'utils/constants';
import { IAdminTopUp, Order } from 'utils/contracts';
import { package_options } from 'utils/Formatter';
import { getErrorHtmlContent, getUnlimitedPackageName } from 'utils/utils';
import * as Yup from 'yup';
import PackageTransaction from './PackageTransaction';

const getActiveUnlimitedPackageId = (organization: any): string | null => {
  if (!organization?.transactions) return null;
  
  const now = new Date();
  const transactions = Object.values(organization.transactions || {});
  
  const activeUnlimited: any = transactions.find((t: any) => 
    t.status === 'success' && 
    t.unlimited === true &&
    t.type === 'CREDIT' &&
    new Date(t.expiry_date) > now
  );
  
  return activeUnlimited ? activeUnlimited.plan_id : null;
};

const getAccumulatedExpiryDate = (organization: any): Date | null => {
  if (!organization?.transactions) return null;
  
  const now = new Date();
  const transactions = Object.values(organization.transactions || {}) as any[];
  
  // Filter only ACTIVE packages (status === 'success' excludes cancelled packages)
  // This ensures that if an older package is cancelled, it won't be considered
  const activeUnlimitedPackages = transactions.filter((t: any) => 
    t.status === 'success' &&  // Only non-cancelled packages
    t.unlimited === true &&
    t.type === 'CREDIT' &&
    t.expiry_date &&
    new Date(t.expiry_date) > now  // Only non-expired packages
  );
  
  if (activeUnlimitedPackages.length === 0) return null;
  
  // Find the package with the most recent EXPIRY DATE (not creation date)
  // This is the accumulated expiry date from all active packages
  const latestPackage: any = activeUnlimitedPackages.reduce((latest: any, current: any) => {
    const latestDate = new Date(latest.expiry_date);
    const currentDate = new Date(current.expiry_date);
    return currentDate > latestDate ? current : latest;
  });
  
  return new Date(latestPackage.expiry_date);
};

let DEFAULT_ORDER = 'desc',
  DEFAULT_ORDER_BY = 'createdAt';

const textFieldStyles = {
  '& .MuiInputBase-root': {
    height: '50px',
    padding: '20px 0px',
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(12px, 14px) scale(1)',
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
    transform: 'translate(12px, -10px) scale(0.75)',
  },
};

// Filter to only show unlimited packages
const unlimitedPackageOptions = package_options.filter(
  (option: any) => option.value.startsWith('PACKAGE_UNLIMITED_')
);

const BusinessPackages = ({ organizations, refetch, mode }: any) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [order, setOrder] = useState<Order>(DEFAULT_ORDER as Order);
  const [orderBy, setOrderBy] = useState<string>(DEFAULT_ORDER_BY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [transactionToCancel, setTransactionToCancel] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const { role } = useContext(UseAuthContext);
  const queryClient = useQueryClient();

  const { 
    data: transactionsData,
    refetch: refetchTransactions 
  } = useQuery(
    ['transactions', organizations?.id, page, rowsPerPage, order, orderBy],
    () => getOrganizationTransactions(organizations?.id, {
      page: page + 1,
      pageSize: rowsPerPage,
      sortOrder: order,
      sortBy: orderBy
    }),
    {
      enabled: mode !== 'create' && !!organizations?.id,
      refetchOnWindowFocus: true,
      onError: (error: any) => {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
    }
  );

  // Filter transactions to show only unlimited packages
  const packagesTransactions = useMemo(() => {
    if (!transactionsData?.data) return [];
    return transactionsData.data.filter((t: any) => t.unlimited === true);
  }, [transactionsData?.data]);

  const formik = useFormik({
    initialValues: {
      packageType: '',
    },
    validationSchema: Yup.object({
      packageType: Yup.string().required(t('FIELD_REQUIRED')),
    }),
    onSubmit: async (values) => {
      if (mode === 'create') {
        enqueueSnackbar(t('BUSINESS_ADMIN_CREDIT_CREATE_FIRST'), {
          variant: 'warning',
        });
        return;
      }

      const topUpData: IAdminTopUp = {
        ProdQuantity: 1,
        OrganizationId: organizations.id,
        ProductDesc: '',
        TransactionId: shortid.generate(),
        ProductID: values.packageType,
      };

      topUpMutation(topUpData);
      refetchTransactions();
    },
  });

  const { mutate: topUpMutation, isLoading: isTopUpLoading } = useMutation(adminTopUp, {
    onSuccess: () => {
      enqueueSnackbar(t('BUSINESS_ADMIN_TRANSACTION_CREDIT_ADD_SUCCESS'), {
        variant: 'success',
      });
      queryClient.invalidateQueries(['organizations']);
      queryClient.invalidateQueries(['transactions']);
      refetch();
      formik.resetForm();
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      console.error('Error during top up:', error);
    },
  });

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const createSortHandler = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleCancelClick = (transactionId: string) => {
    setTransactionToCancel(transactionId);
    setCancellationReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelTransaction = async (transactionId: string, reason?: string) => {
    try {
      await cancelTransaction(organizations.id, transactionId, reason);
      enqueueSnackbar(t('TRANSACTION_CANCELLED_SUCCESS'), { variant: 'success' });
      refetchTransactions();
      // Refresh organization data to update hasUnlimited flag
      queryClient.invalidateQueries(['getOrganizationById', organizations.id]);
      refetch();
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    }
  };

  const handleConfirmCancel = async () => {
    if (transactionToCancel) {
      await handleCancelTransaction(transactionToCancel, cancellationReason || undefined);
    }
    setCancelDialogOpen(false);
    setTransactionToCancel(null);
    setCancellationReason('');
  };

  const accumulatedExpiryDate = getAccumulatedExpiryDate(organizations);

  return (
    <Box>
      <Box className='business_packages_header'>
        {accumulatedExpiryDate && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '30px' }}>
            <TypographyLocale variant='h2' fontWeight='bold'>
              <span>{t('UNLIMITED_UNTIL')} {moment.utc(accumulatedExpiryDate).local().format('DD/MM/YYYY')}</span>
            </TypographyLocale>
          </Box>
        )}
        {role === USER_ROLES.ADMIN && mode !== 'create' && (
          <form onSubmit={formik.handleSubmit} style={{ width: "100%", marginBottom: "30px" }}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Autocomplete
                  clearIcon={<></>}
                  options={unlimitedPackageOptions}
                  getOptionLabel={(option: any) => t(option.label)}
                  onChange={(_, value: any) => {
                    formik.setFieldValue('packageType', value?.value);
                  }}
                  size='small'
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="packageType"
                      label={t("ADMIN_ADD_BUSINESS_CREDIT_PACKAGE_SELECT")}
                      sx={{ ...textFieldStyles, width: "220px" }}
                      required
                    />
                  )}
                />
                <ButtonCustom
                  color="primary"
                  type="submit"
                  variant="contained"
                  disabled={isTopUpLoading}
                  startIcon={isTopUpLoading ? <CircularProgress size={20} color='inherit'/> : null}
                >
                  {t("SAVE_BUTTON")}
                </ButtonCustom>
              </Box>
            </Box>
          </form>
        )}

        {mode !== 'create' && (
          packagesTransactions.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <CustomTableFontCell sx={{textAlign: 'center'}}>
                      <StyledTableSortLabel
                        active={orderBy === "type"}
                        direction={orderBy === "type" ? order : "asc"}
                        onClick={() => createSortHandler("type")}
                      >
                        {t('BUSINESS_SETTING_TRANSACTION_STATUS')}
                        {orderBy === "type" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </Box>
                        ) : null}
                      </StyledTableSortLabel>
                    </CustomTableFontCell>
                    <CustomTableFontCell>
                      <StyledTableSortLabel
                        active={orderBy === "createdAt"}
                        direction={orderBy === "createdAt" ? order : "asc"}
                        onClick={() => createSortHandler("createdAt")}
                      >
                        {t('BUSINESS_SETTING_TRANSACTION_DATE')}
                        {orderBy === "createdAt" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </Box>
                        ) : null}
                      </StyledTableSortLabel>
                    </CustomTableFontCell>
                    <CustomTableFontCell>
                      <StyledTableSortLabel
                        active={orderBy === "project"}
                        direction={orderBy === "project" ? order : "asc"}
                        onClick={() => createSortHandler("project")}
                      >
                        {t('EDIT_BUSINESS_TABLE_FOR')}
                        {orderBy === "project" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </Box>
                        ) : null}
                      </StyledTableSortLabel>
                    </CustomTableFontCell>
                    <CustomTableFontCell>
                      <StyledTableSortLabel
                        active={orderBy === "expiry_date"}
                        direction={orderBy === "expiry_date" ? order : "asc"}
                        onClick={() => createSortHandler("expiry_date")}
                      >
                        {t('BUSINESS_SETTING_TRANSACTION_EXPIRES_AT')}
                        {orderBy === "expiry_date" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </Box>
                        ) : null}
                      </StyledTableSortLabel>
                    </CustomTableFontCell>
                    <CustomTableFontCell>
                      <StyledTableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => createSortHandler("status")}
                      >
                        {t('PACKAGE_STATUS')}
                        {orderBy === "status" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </Box>
                        ) : null}
                      </StyledTableSortLabel>
                    </CustomTableFontCell>
                    <CustomTableFontCell>
                      {t('ADMIN_TRANSACTION_CANCELLATION_REASON')}
                    </CustomTableFontCell>
                    {role === USER_ROLES.ADMIN && (
                      <CustomTableFontCell align="center">
                        {t('ADMIN_REVISION_ACTIONS')}
                      </CustomTableFontCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packagesTransactions.map((transaction) => (
                    <PackageTransaction
                      key={transaction.id}
                      transaction={transaction}
                      onCancel={role === USER_ROLES.ADMIN ? handleCancelClick : undefined}
                      isAdmin={role === USER_ROLES.ADMIN}
                    />
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                sx={{ width: "100%" }}
                rowsPerPageOptions={[20]}
                component='div'
                count={transactionsData?.paging?.total ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('MUI_ROWS_PER_PAGE')}
                labelDisplayedRows={({ from, to, count }) => t('MUI_PAGINATION_RANGE', { from, to, count })}
                getItemAriaLabel={(type) => t(`MUI_PAGINATION_${type.toUpperCase()}_PAGE`)}
              />
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>{t('NO_PACKAGES_TRANSACTIONS')}</Typography>
            </Box>
          )
        )}
      </Box>
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => {
          setCancelDialogOpen(false);
          setCancellationReason('');
        }}
        maxWidth="sm"
        fullWidth
        sx={{ marginBottom: "50px" }}
      >
        <DialogTitle>
          <ButtonIcon
            aria-label="close"
            onClick={() => {
              setCancelDialogOpen(false);
              setCancellationReason('');
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </ButtonIcon>
          <Typography variant='h2' sx={{ textAlign: 'center' }}>
            {t("TRANSACTION_CANCEL_CONFIRM_TITLE")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography textAlign={"center"} color='#2F3B49' fontSize={"18px"} sx={{ mb: 2 }}>
            {t("TRANSACTION_CANCEL_CONFIRM_MESSAGE")}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t("CANCELLATION_REASON_LABEL")}
            placeholder={t("CANCELLATION_REASON_PLACEHOLDER")}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions dir='ltr' sx={{ 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'space-between', 
          paddingBottom: "20px", 
          paddingX:"24px",
          '& > :not(style) ~ :not(style)': {
            marginLeft: '0 !important'
          },
          '& > *': {
            marginLeft: '0 !important'
          }
        }}>
          <ButtonCustom 
            onClick={() => {
              setCancelDialogOpen(false);
              setCancellationReason('');
            }} 
            color='secondary' 
            className='secondary-btn'
            variant="outlined"
          >
            {t("CLOSE_BUTTON")}
          </ButtonCustom>
          <ButtonCustom 
            onClick={handleConfirmCancel} 
            color="primary" 
            variant="contained"
          >
            {t("CANCEL_BUTTON")}
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessPackages;
